import { StateCreator } from 'zustand';
import { Edge, EdgeChange, applyEdgeChanges } from '@xyflow/react';
import { PipelineSlice } from './pipelineSlice';

export interface EdgeSlice {
  // Edge operations
  setEdges: (edges: Edge[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  addEdge: (edge: Edge) => void;
  updateEdge: (pipelineId: string, edgeId: string, updates: Partial<Edge>) => void;
  deleteEdge: (edgeId: string) => void;
  updateEdgeData: (edgeId: string, data: Record<string, unknown>) => void;
}

export const createEdgeSlice: StateCreator<
  PipelineSlice & EdgeSlice,
  [],
  [],
  EdgeSlice
> = (set) => ({
  setEdges: (edges) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId ? { ...p, edges } : p
        ),
      };
    }),

  onEdgesChange: (changes) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      const pipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
      if (!pipeline) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId
            ? { ...p, edges: applyEdgeChanges(changes, p.edges) }
            : p
        ),
      };
    }),

  addEdge: (edge) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      const pipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
      if (!pipeline) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId ? { ...p, edges: [...p.edges, edge] } : p
        ),
      };
    }),

  updateEdge: (pipelineId, edgeId, updates) =>
    set((state) => {
      const pipeline = state.pipelines.find((p) => p.id === pipelineId);
      if (!pipeline) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === pipelineId
            ? {
                ...p,
                edges: p.edges.map((e) =>
                  e.id === edgeId ? { ...e, ...updates } : e
                ),
              }
            : p
        ),
      };
    }),

  deleteEdge: (edgeId) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      const pipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
      if (!pipeline) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId
            ? { ...p, edges: p.edges.filter((e) => e.id !== edgeId) }
            : p
        ),
      };
    }),

  updateEdgeData: (edgeId, data) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      const pipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
      if (!pipeline) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId
            ? {
                ...p,
                edges: p.edges.map((e) =>
                  e.id === edgeId ? { ...e, data: { ...e.data, ...data } } : e
                ),
              }
            : p
        ),
      };
    }),
});

